import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2, PackagePlus, CheckCircle2, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGrn } from "@/features/grns/api";
import { GrnKindBadge } from "@/features/grns/badges";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canCreateAssetFromGRN } from "@/lib/roles";
import { apiList } from "@/integrations/api/client";
import { useAssetsByGrn } from "@/features/assets/generateApi";
import { useLookups } from "@/features/assets/api";
import { useMemo } from "react";

export const Route = createFileRoute("/_authenticated/grns/$grnId/")({
  component: GrnDetail,
});

function useGrnLinkedAssets(grnId: string | undefined) {
  return useQuery({
    queryKey: ["grn_linked_assets", grnId],
    enabled: !!grnId,
    queryFn: async () => {
      try {
        const res = await apiList<{ id: string; asset_code: string; name: string; source_grn_item_index: number | null }>(
          "/api/v1/assets/",
          { source_grn_id_in: grnId!, limit: 1000 }
        );
        return res.results || [];
      } catch (e) {
        return [];
      }
    },
  });
}

function statusVariant(s: string): "default" | "secondary" | "outline" {
  if (s === "Created") return "secondary";
  if (s === "Partially Created") return "default";
  return "outline";
}

function GrnDetail() {
  const { grnId } = Route.useParams();
  const navigate = useNavigate();
  const { data: grn, isLoading } = useGrn(grnId);
  const { data: me } = useCurrentUser();
  const { data: linkedAssets = [] } = useGrnLinkedAssets(grnId);
  const { data: procAssets = [] } = useAssetsByGrn(grnId);
  const { data: lookups } = useLookups();
  const catById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of lookups?.categories ?? []) m.set(c.id, c.name);
    return m;
  }, [lookups]);

  if (isLoading) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!grn) return <div className="p-6 text-sm text-muted-foreground">GRN not found.</div>;

  const roles = me?.roles ?? [];
  const isAssetGrn = grn.kind === "asset";
  const hasNormalisedItems = (grn.grn_items?.length ?? 0) > 0;

  // Legacy single-shot GRN prefill still supported for legacy (non-normalised) GRNs
  const linkedForLine0 = linkedAssets.find((a) => (a.source_grn_item_index ?? 0) === 0);
  const canLegacyCreate = isAssetGrn && !hasNormalisedItems && canCreateAssetFromGRN(roles) && !linkedForLine0;

  // Batch create eligibility (new normalised flow)
  const canBatchCreate = Boolean(
    isAssetGrn &&
      hasNormalisedItems &&
      canCreateAssetFromGRN(roles) &&
      grn?.grn_items?.some(
        (it: any) => it.asset_creation_required && Number(it.accepted_quantity) - Number(it.created_asset_count ?? 0) > 0,
      ),
  );

  const anyStorePending = (grn.grn_items ?? []).some(
    (it: any) => Number(it.accepted_quantity) > 0 && it.asset_creation_status !== "received_to_store"
  );
  const canStoreAllocate = !isAssetGrn && hasNormalisedItems && canCreateAssetFromGRN(roles) && anyStorePending;

  const handleLegacyCreate = () => {
    const item = (grn.items ?? [])[0] ?? {};
    sessionStorage.setItem("grn_asset_prefill", JSON.stringify({
      grn_id: grn.id,
      grn_no: grn.grn_no,
      grn_item_index: 0,
      vendor_id: grn.vendor_id ?? null,
      po_number: grn.po_number ?? null,
      invoice_no: grn.invoice_no ?? null,
      invoice_date: grn.invoice_date ?? null,
      items: grn.items ?? [],
      item,
    }));
    navigate({ to: "/assets/new" });
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/grns" })}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span className="font-mono text-base">{grn.grn_no}</span>
              {isAssetGrn && !hasNormalisedItems && (linkedForLine0 ? (
                <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3" />Asset Created</Badge>
              ) : (
                <Badge variant="outline">Asset Pending</Badge>
              ))}
              {grn.is_legacy && <Badge variant="outline">Legacy GRN</Badge>}
            </span>
            <span className="flex flex-wrap items-center gap-2">
              <GrnKindBadge kind={grn.kind} />
              {canBatchCreate && (
                <Button size="sm" onClick={() => navigate({ to: "/grns/$grnId/generate-assets", params: { grnId } })}>
                  <PackagePlus className="mr-1 h-4 w-4" />Create Assets
                </Button>
              )}
              {canStoreAllocate && (
                <Button size="sm" onClick={() => navigate({ to: "/grns/$grnId/generate-assets", params: { grnId } })}>
                  <Warehouse className="mr-1 h-4 w-4" />Allocate to Store
                </Button>
              )}
              {canLegacyCreate && (
                <Button size="sm" variant="secondary" onClick={handleLegacyCreate}>
                  <PackagePlus className="mr-1 h-4 w-4" />Create Asset (legacy)
                </Button>
              )}
              {isAssetGrn && !hasNormalisedItems && !canLegacyCreate && !linkedForLine0 && (
                <span className="text-xs text-muted-foreground">Asset generation is available for normalised GRNs only.</span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-4">
          <div><div className="text-xs text-muted-foreground">Vendor</div><div>{grn.vendor?.name ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">PO Number</div><div className="font-mono text-xs">{grn.po_number ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Invoice No</div><div className="font-mono text-xs">{grn.invoice_no ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Invoice Date</div><div>{grn.invoice_date ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">Received Date</div><div>{grn.received_date}</div></div>
          <div className="md:col-span-3"><div className="text-xs text-muted-foreground">Indent</div>
            <div>{grn.indent ? <Link to="/indents/$indentId" params={{ indentId: grn.indent.id }} className="font-mono text-xs text-primary hover:underline">{grn.indent.indent_no}</Link> : "—"}</div>
          </div>
          <div className="md:col-span-4"><div className="text-xs text-muted-foreground">Condition Remarks</div><div>{grn.condition_remarks || "—"}</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Items</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {hasNormalisedItems ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="w-10">#</TableHead>
                    {grn.kind === "asset" && <TableHead>Asset Type</TableHead>}
                    <TableHead>
                      {grn.kind === "asset" ? "Asset Name" : grn.kind === "spare" ? "Spare Name" : "Consumable Name"}
                    </TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead className="text-right">Received</TableHead>
                    <TableHead className="text-right">Good</TableHead>
                    <TableHead className="text-right">Pkg Damaged</TableHead>
                    <TableHead className="text-right">Defective</TableHead>
                    <TableHead className="text-right">Shortage</TableHead>
                    <TableHead className="text-right">Accepted</TableHead>
                    <TableHead className="text-right">Unit ₹</TableHead>
                    <TableHead className="text-right">Tax ₹</TableHead>
                    <TableHead className="text-right">Line ₹</TableHead>
                    {grn.kind === "asset" && <TableHead>Asset creation</TableHead>}
                  </TableRow></TableHeader>
                  <TableBody>
                    {grn.grn_items!.map((it: any) => {
                      const created = Number(it.created_asset_count ?? 0);
                      const accepted = Number(it.accepted_quantity);
                      const ordered = Number(it.quantity_ordered);
                      const received = Number(it.quantity_received);
                      const good = Number(it.good_quantity ?? 0);
                      const pkg = Number(it.package_damaged_quantity ?? 0);
                      const defective = Number(it.defective_quantity ?? 0);
                      const shortage = Number(it.shortage_quantity ?? 0);
                      const pending = Math.max(0, ordered - received);
                      const assetType = it.asset_class || it.asset_type || "—";
                      const assetName =
                        (it.asset_category_id ? catById.get(it.asset_category_id) : null) ??
                        it.item_name ??
                        "—";
                      return (
                        <TableRow key={it.id}>
                          <TableCell>{it.line_no}</TableCell>
                          {grn.kind === "asset" && <TableCell className="text-xs">{assetType}</TableCell>}
                          <TableCell>
                            <div>{assetName}</div>
                            {it.item_description && <div className="text-xs text-muted-foreground">{it.item_description}</div>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{ordered}</TableCell>
                          <TableCell className="text-right tabular-nums">{pending}</TableCell>
                          <TableCell className="text-right tabular-nums">{received}</TableCell>
                          <TableCell className="text-right tabular-nums">{good}</TableCell>
                          <TableCell className="text-right tabular-nums">{pkg}</TableCell>
                          <TableCell className="text-right tabular-nums">{defective}</TableCell>
                          <TableCell className="text-right tabular-nums">{shortage}</TableCell>
                          <TableCell className="text-right tabular-nums">{accepted}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(it.unit_price).toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(it.tax_amount).toFixed(2)}</TableCell>
                          <TableCell className="text-right tabular-nums">{Number(it.line_total).toFixed(2)}</TableCell>
                          {grn.kind === "asset" && (
                            <TableCell>
                              {it.asset_creation_required ? (
                                <Badge variant={statusVariant(it.asset_creation_status)} className="text-xs">
                                  {it.asset_creation_status} ({created}/{accepted})
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">Not required</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {(() => {
                const t = grn.grn_items!.reduce((a: { rec: number, good: number, pkg: number, def: number, short: number, acc: number, sub: number, tax: number, grand: number }, r: any) => {
                  a.rec += Number(r.quantity_received);
                  a.good += Number(r.good_quantity ?? 0);
                  a.pkg += Number(r.package_damaged_quantity ?? 0);
                  a.def += Number(r.defective_quantity ?? 0);
                  a.short += Number(r.shortage_quantity ?? 0);
                  a.acc += Number(r.accepted_quantity);
                  a.sub += Number(r.accepted_quantity) * Number(r.unit_price);
                  a.tax += Number(r.tax_amount);
                  a.grand += Number(r.line_total);
                  return a;
                }, { rec: 0, good: 0, pkg: 0, def: 0, short: 0, acc: 0, sub: 0, tax: 0, grand: 0 });
                return (
                  <div className="flex flex-wrap justify-between gap-3 border-t p-3 text-sm">
                    <div className="space-x-3 text-muted-foreground">
                      <span>Received: <b className="text-foreground">{t.rec}</b></span>
                      <span>Good: <b className="text-foreground">{t.good}</b></span>
                      <span>Pkg Damaged: <b className="text-foreground">{t.pkg}</b></span>
                      <span>Defective: <b className="text-foreground">{t.def}</b></span>
                      <span>Shortage: <b className="text-foreground">{t.short}</b></span>
                      <span>Accepted: <b className="text-foreground">{t.acc}</b></span>
                    </div>
                    <div className="space-x-4">
                      <span>Subtotal: <b>₹ {t.sub.toFixed(2)}</b></span>
                      <span>Tax: <b>₹ {t.tax.toFixed(2)}</b></span>
                      <span>Grand: <b>₹ {t.grand.toFixed(2)}</b></span>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Description</TableHead><TableHead>Qty</TableHead><TableHead>Unit Cost</TableHead><TableHead>Store / Serial</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(grn.items ?? []).map((it: any, i: any) => (
                  <TableRow key={i}>
                    <TableCell>{it.description}</TableCell>
                    <TableCell>{it.qty}</TableCell>
                    <TableCell>₹ {Number(it.unit_cost ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-xs">{it.store_location ?? it.serial_no ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {isAssetGrn && procAssets.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Created Assets ({procAssets.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Asset Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Serial</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Status</TableHead>

                </TableRow></TableHeader>
                <TableBody>
                  {procAssets.map((a) => {
                    const item = grn.grn_items?.find((it: any) => it.id === a.grn_item_id || it.line_no === (a as any).source_grn_item_index);
                    const displayType = a.asset_class || a.asset_type || item?.asset_class || item?.asset_type || "—";
                    return (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Link to="/assets/$assetId" params={{ assetId: a.id }} className="font-mono text-xs text-primary hover:underline">{a.asset_code}</Link>
                      </TableCell>
                      <TableCell>{a.name}</TableCell>
                      <TableCell className="text-xs">{displayType}</TableCell>
                      <TableCell className="text-xs">{a.serial_number ?? "—"}</TableCell>
                      <TableCell className="text-xs">{a.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{a.location?.name ?? "—"}</TableCell>
                      <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>

                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
